import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Phone, X } from 'lucide-react';
import { Button } from '@ridemaadi/ui';
import { geofenceService } from '../../navigation/services/GeofenceService';
import { useTriggerSos } from '../activity';
import { SlideToSendSos } from './SlideToSendSos';

const HELPLINES = [
  { label: 'Call Police', tel: '100' },
  { label: 'Call Ambulance', tel: '102' },
  { label: 'Highway Helpline', tel: '1033' },
] as const;

/** Emergency options sheet - slide to send SOS; calls do not auto-alert. */
export function SosBottomSheet({
  rideId,
  open,
  onClose,
  onSent,
}: {
  rideId: string;
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}) {
  const trigger = useTriggerSos(rideId);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const sendSos = async () => {
    let location: { latitude: number; longitude: number } | undefined;
    try {
      const pos = await geofenceService.getCurrentPosition();
      location = { latitude: pos.lat, longitude: pos.lng };
    } catch {
      /* optional location */
    }
    await trigger.mutateAsync(location);
    onSent();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40"
      style={{ paddingBottom: 'var(--vv-keyboard-inset, 0px)' }}
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-t-2xl border-t border-border bg-surface shadow-2xl"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sos-sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-3">
          <h2 id="sos-sheet-title" className="text-base font-semibold">
            🚨 Emergency &amp; Safety
          </h2>
          <button
            type="button"
            className="rounded-lg p-2 text-muted hover:bg-surface-alt"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-4 pb-4">
          <SlideToSendSos onSend={() => void sendSos()} loading={trigger.isPending} />

          <div className="space-y-2">
            {HELPLINES.map(({ label, tel }) => (
              <a
                key={tel}
                href={`tel:${tel}`}
                className="flex min-h-touch items-center gap-3 rounded-xl border border-border px-4 py-3 text-sm font-medium hover:bg-surface-alt"
              >
                <Phone className="h-4 w-4 text-primary" aria-hidden />
                {label}
              </a>
            ))}
          </div>

          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
